import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Switch, Tabs, Alert, Space, Typography, Divider, Tag, message } from 'antd';
import { SaveOutlined, GlobalOutlined, MobileOutlined, GoogleOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Text, Title } = Typography;

const PLATFORMS = [
  { key: 'site_fa', label: '🇮🇷 سایت فارسی', icon: <GlobalOutlined />, color: '#722f37' },
  { key: 'site_en', label: '🇬🇧 سایت انگلیسی', icon: <GlobalOutlined />, color: '#1677ff' },
  { key: 'app',     label: '📱 اپلیکیشن',     icon: <MobileOutlined />, color: '#52c41a' },
];

export default function AnalyticsTrackingManager() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAnalyticsTrackingConfig();
      if (res.data.success) {
        setConfig(res.data.config);
        form.setFieldsValue(res.data.config);
      }
    } catch {
      message.error('خطا در بارگذاری تنظیمات تگ منیجر/آنالیتیکس');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue(true);
      const res = await adminAPI.updateAnalyticsTrackingConfig(values);
      if (res.data.success) {
        message.success('تنظیمات تگ منیجر/آنالیتیکس ذخیره شد ✓');
        setConfig(res.data.config);
      } else {
        message.error(res.data.message || 'خطا در ذخیره');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در اتصال');
    } finally {
      setSaving(false);
    }
  };

  const PlatformCard = ({ platform }) => {
    const { key, label, color } = platform;
    const isEnabled = Form.useWatch([key, 'enabled'], form);

    return (
      <Card
        key={key}
        style={{
          marginBottom: 16,
          borderRadius: 12,
          border: isEnabled ? `2px solid ${color}` : '1px solid #f0f0f0',
          background: isEnabled ? `linear-gradient(135deg, ${color}08 0%, #ffffff 100%)` : '#fafafa',
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Space>
            <Tag color={isEnabled ? color : 'default'} style={{ fontSize: 14, padding: '4px 12px' }}>
              {label}
            </Tag>
          </Space>
          <Form.Item name={[key, 'enabled']} valuePropName="checked" style={{ marginBottom: 0 }}>
            <Switch
              checkedChildren="فعال"
              unCheckedChildren="غیرفعال"
            />
          </Form.Item>
        </div>

        <div style={{ opacity: isEnabled ? 1 : 0.5, pointerEvents: isEnabled ? 'auto' : 'none', transition: 'opacity 0.3s' }}>
          <Form.Item
            label={
              <Space>
                <GoogleOutlined style={{ color: '#4285F4' }} />
                <span>Google Tag Manager ID</span>
              </Space>
            }
            name={[key, 'gtmId']}
            help="مثال: GTM-XXXXXXX"
            rules={[
              {
                pattern: /^(GTM-[A-Z0-9]+)?$/,
                message: 'فرمت GTM ID صحیح نیست (مثال: GTM-ABC123)',
              },
            ]}
          >
            <Input
              placeholder="GTM-XXXXXXX"
              style={{ fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <GoogleOutlined style={{ color: '#EA4335' }} />
                <span>Google Analytics ID</span>
              </Space>
            }
            name={[key, 'gaId']}
            help="مثال: G-XXXXXXXXXX یا UA-XXXXXXXXX-X"
            rules={[
              {
                pattern: /^((G-[A-Z0-9]+)|(UA-\d+-\d+))?$/,
                message: 'فرمت GA ID صحیح نیست (مثال: G-ABC123XYZ یا UA-12345678-1)',
              },
            ]}
          >
            <Input
              placeholder="G-XXXXXXXXXX"
              style={{ fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
              allowClear
            />
          </Form.Item>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 20, borderRadius: 8 }}
        message="تنظیمات Google Tag Manager و Google Analytics"
        description={
          <div>
            <Text>
              در اینجا می‌توانید شناسه‌های GTM و GA را برای هر پلتفرم (سایت فارسی، سایت انگلیسی و اپلیکیشن) به‌طور جداگانه تنظیم کنید.
              شناسه‌ها پس از ذخیره، به‌صورت خودکار در صفحات مربوطه بارگذاری می‌شوند.
            </Text>
          </div>
        }
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          site_fa: { gtmId: '', gaId: '', enabled: false },
          site_en: { gtmId: '', gaId: '', enabled: false },
          app:     { gtmId: '', gaId: '', enabled: false },
        }}
      >
        {PLATFORMS.map((platform) => (
          <PlatformCard key={platform.key} platform={platform} />
        ))}

        <Divider />

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
            size="large"
            style={{ borderRadius: 8 }}
          >
            ذخیره تنظیمات
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadConfig}
            loading={loading}
            size="large"
            style={{ borderRadius: 8 }}
          >
            بارگذاری مجدد
          </Button>
        </Space>
      </Form>

      <Divider />

      <Card
        size="small"
        title="🔗 مسیر API عمومی"
        style={{ borderRadius: 8, background: '#f6f8fa' }}
      >
        <Text code style={{ direction: 'ltr', display: 'block' }}>
          GET /api/public/analytics-tracking?platform=site_fa|site_en|app
        </Text>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          این مسیر بدون احراز هویت قابل دسترسی است و شناسه‌های فعال‌شده را برمی‌گرداند. برای بارگذاری
          دینامیک اسکریپت‌های GTM/GA در فرانت‌اند یا اپلیکیشن از این مسیر استفاده کنید.
        </Text>
      </Card>
    </div>
  );
}
